import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { downloadBlob } from "@/features/utils/blob";
import type { Tone } from "@/features/utils/colors";
import {
  AdminAPI,
  useRunSyntheticCall,
  useSyntheticCall,
  useSyntheticCalls,
  type SyntheticCallCheck,
  type SyntheticCallRun,
  type SyntheticCallStatus,
} from "@/features/admin";
import { useState } from "react";
import { toast } from "sonner";
import { AdminPageLayout } from "../layout";
import { StatCard } from "./components/stat-card";

const ms = (n?: number | null) =>
  n == null || !Number.isFinite(n)
    ? "—"
    : n >= 1000
      ? `${(n / 1000).toFixed(2)} s`
      : `${Math.round(n)} ms`;

const when = (at: number) =>
  at ? new Date(at).toLocaleString("fr-FR") : "jamais";

/**
 * Load an export and save it, turning a failure into a toast.
 *
 * Loading is the caller's (the two exports have different routes); saving is
 * `downloadBlob`. What this adds is the error message, which is why it stays here.
 */
const saveCsv = async (load: () => Promise<Blob>, filename: string) => {
  try {
    downloadBlob(await load(), filename);
  } catch (error) {
    toast.error(
      error instanceof Error ? error.message : "Export impossible",
    );
  }
};

/**
 * Why the conversation stopped (backend `CallEndReason`). Shown on every run, not only the
 * failed ones: a transcript that ends mid-tool-call reads as a crash when the simulated
 * customer had simply said goodbye, and the two need very different fixes.
 */
const END_REASONS: Record<string, string> = {
  caller_hangup: "client a raccroché",
  caller_silent: "client sans réponse",
  agent_hangup: "agent a raccroché",
  max_turns: "limite de tours",
  deadline: "limite de durée",
  harness_error: "erreur du harnais",
};

/**
 * Which of the three customers the run played. A tenant's agent does not meet one kind of
 * caller, and each takes a different path: an unknown number has no record to greet, a known
 * one should be recognised, and one the SMS cannot reach forces the agent to ask for an
 * e-mail before it can book at all.
 */
const CALLER_KINDS: Record<string, { label: string; hint: string }> = {
  new: {
    label: "nouveau client",
    hint: "Numéro jamais vu : l'agent doit créer la fiche et demander qui l'appelle.",
  },
  known: {
    label: "client connu",
    hint: "Le même numéro à chaque run : sa fiche existe, l'agent devrait le reconnaître.",
  },
  no_sms: {
    label: "numéro sans SMS",
    hint: "La confirmation ne peut pas partir en SMS : l'agent doit demander un e-mail et réserver quand même.",
  },
};

/** Older runs recorded no `kind`, only whether the SMS could reach the number. */
const callerKind = (contact: { kind?: string; smsCapable: boolean }): string =>
  contact.kind || (contact.smsCapable ? "known" : "no_sms");

/** `error` is grey, not red: the harness broke and nothing was proven either way. */
const STATUS: Record<SyntheticCallStatus, { label: string; tone: Tone }> = {
  ok: { label: "Réussi", tone: "green" },
  degraded: { label: "Dégradé", tone: "yellow" },
  failed: { label: "Échec", tone: "red" },
  error: { label: "Non concluant", tone: "gray" },
};

function StatusBadge({ status }: { status: SyntheticCallStatus }) {
  const style = STATUS[status] || STATUS.error;
  return <Badge color={style.tone}>{style.label}</Badge>;
}

/** The transcript of one run, plus what the robot's own STT made of each answer. */
function RunDetail({ runId }: { runId: string }) {
  const { data: run, isLoading } = useSyntheticCall(runId);

  if (isLoading || !run) return <Skeleton className="h-40 w-full" />;

  const lines = run.transcript?.lines || [];
  const heard = run.heard?.turns || [];

  return (
    <div className="p-4 bg-muted/30 border-t space-y-3">
      <div className="flex justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            saveCsv(
              () => AdminAPI.getSyntheticCallCsv(runId),
              `appel-de-test_${runId}.csv`,
            )
          }
          title="Le résumé, les vérifications, les constats du verdict, la conversation et ce que le robot a entendu — dans un seul fichier"
        >
          Exporter ce test (CSV)
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="text-xs font-medium text-muted-foreground">
            Conversation (telle qu'enregistrée par le pipeline)
          </div>
        {lines.length ? (
          <div className="space-y-1 max-h-96 overflow-y-auto pr-2">
            {lines.map((line, i) => (
              <div key={i} className="text-sm">
                <span
                  className={
                    line.role === "agent"
                      ? "text-primary font-medium"
                      : line.role === "tool"
                        ? "text-muted-foreground font-mono text-xs"
                        : "font-medium"
                  }
                >
                  {line.role === "caller"
                    ? "client"
                    : line.role === "tool"
                      ? `outil ${line.tool?.name}`
                      : line.role}
                  {" : "}
                </span>
                <span
                  className={
                    line.tool?.status === "error" ? "text-red-600" : undefined
                  }
                >
                  {line.role === "tool"
                    ? line.tool?.status === "error"
                      ? "erreur"
                      : "ok"
                    : line.text}
                </span>
                {line.response_delay_ms ? (
                  <span className="ml-2 text-xs text-muted-foreground">
                    ({ms(line.response_delay_ms)})
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">
            Aucun message enregistré — l'appel n'a produit aucune session.
          </div>
        )}
      </div>

      <div className="space-y-2">
        <div className="text-xs font-medium text-muted-foreground">
          Ce que le client simulé a entendu (STT sur l'audio reçu)
        </div>
        <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
          {heard.map((turn, i) => (
            <div key={i} className="text-sm border-l-2 pl-2">
              {turn.said ? (
                <div className="text-muted-foreground">« {turn.said} »</div>
              ) : (
                <div className="text-muted-foreground">(début d'appel)</div>
              )}
              <div className={turn.dead_air ? "text-red-600" : undefined}>
                {turn.dead_air
                  ? "— aucun son de l'agent —"
                  : turn.heard || "— audio non transcrit —"}
              </div>
              <div className="text-xs text-muted-foreground">
                réponse en {ms(turn.reply_delay_ms)}, {ms(turn.agent_spoke_ms)}{" "}
                de parole
              </div>
            </div>
          ))}
        </div>
        {run.error ? (
          <div className="text-sm text-red-600">Erreur : {run.error}</div>
        ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * What the agent was expected to do, and whether it did.
 *
 * This is the part of the row worth reading first: the verdict is built on these, not on
 * what the caller AI thought it had obtained. A `skipped` check means nothing is being said
 * about the agent there: the robot never played that part of its scenario, or the check had
 * no object at all (asking a known customer for an e-mail it already has on file).
 */
function Checks({ checks }: { checks: SyntheticCallCheck[] }) {
  if (!checks.length) return null;

  const mark = {
    passed: { icon: "✓", className: "text-emerald-600" },
    failed: { icon: "✕", className: "text-red-600" },
    skipped: { icon: "–", className: "text-muted-foreground" },
  };

  return (
    <div className="px-4 pb-3">
      <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
        Comportement attendu
      </div>
      <ul className="space-y-1">
        {checks.map((check) => (
          <li key={check.id} className="text-sm flex gap-2">
            <span className={mark[check.status].className}>
              {mark[check.status].icon}
            </span>
            <span
              className={
                check.status === "failed" && check.required
                  ? "text-red-600"
                  : check.status === "skipped"
                    ? "text-muted-foreground"
                    : ""
              }
            >
              {check.label}
              {check.detail ? (
                <span className="text-muted-foreground"> — {check.detail}</span>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function RunRow({
  run,
  expanded,
  onToggle,
}: {
  run: SyntheticCallRun;
  expanded: boolean;
  onToggle: () => void;
}) {
  const issues = run.issues?.list || [];
  const rescued = run.fallback
    ? run.fallback.called_used + run.fallback.called_failed
    : 0;

  return (
    <Card>
      <CardContent className="p-0">
        <button
          type="button"
          onClick={onToggle}
          className="w-full text-left p-4 flex flex-wrap items-center gap-x-6 gap-y-2 hover:bg-muted/40"
        >
          <StatusBadge status={run.status} />
          <div className="text-sm font-medium min-w-40">
            {when(run.started_at)}
            {run.suite_id ? (
              <span
                className="ml-2 text-xs text-muted-foreground"
                title="Un run est une suite d'appels enchaînés : réserver, déplacer, annuler"
              >
                appel {run.call_index + 1}
              </span>
            ) : null}
          </div>
          <div className="text-sm text-muted-foreground">
            {run.caller_turns} tour(s) client · {run.agent_turns} réponse(s) ·{" "}
            {ms(run.duration_ms)}
            {run.ended_reason ? (
              <>
                {" · "}
                <span title="Pourquoi la conversation s'est arrêtée">
                  {END_REASONS[run.ended_reason] || run.ended_reason}
                </span>
              </>
            ) : null}
          </div>
          {run.scenario_detail?.objectives?.length ? (
            <div
              className="text-sm text-muted-foreground"
              title={`Graine ${run.scenario_detail.seed} — rejouable à l'identique`}
            >
              {run.scenario_detail.objectives
                .map((objective) => objective.label)
                .join(" + ")}
            </div>
          ) : null}
          <div
            className="text-sm"
            title={
              "« Audible » et les percentiles sont mesurés sur la même chose : la première" +
              " parole de l'agent au-dessus du bruit de fond. Le greeting n'entre pas dans" +
              " les percentiles — ce n'est pas une réponse à une question du client." +
              (run.greeting_transcript_ms
                ? ` Compris (STT du robot) en ${ms(run.greeting_transcript_ms)}.`
                : "")
            }
          >
            agent audible en {ms(run.greeting_ms)} · réponse p50{" "}
            {ms(run.reply_delay?.p50)} · p95 {ms(run.reply_delay?.p95)}
            <span className="text-muted-foreground">
              {" "}
              ({run.reply_delay?.count ?? 0} rép.)
            </span>
          </div>
          {run.scenario_detail?.contact ? (
            <div
              className="text-sm text-muted-foreground"
              title={`Appel passé depuis ${run.scenario_detail.contact.from}. ${
                CALLER_KINDS[callerKind(run.scenario_detail.contact)].hint
              }`}
            >
              {CALLER_KINDS[callerKind(run.scenario_detail.contact)].label}
            </div>
          ) : null}
          {run.latency_grade?.count ? (
            <div
              className={`text-sm ${
                run.latency_grade.overLimit?.length
                  ? "text-red-600"
                  : run.latency_grade.score < 100
                    ? "text-amber-600"
                    : "text-emerald-600"
              }`}
              title={`${run.latency_grade.good} réponse(s) sous le seuil, ${run.latency_grade.fair} acceptable(s), ${run.latency_grade.poor} trop lente(s)`}
            >
              réactivité {run.latency_grade.score}%
            </div>
          ) : null}
          {rescued ? (
            <div className="text-sm text-amber-600">
              fallback ×{rescued}
            </div>
          ) : null}
          {run.tool_errors ? (
            // Red only for an error the agent never got past. A tool refusing a slot taken two
            // seconds ago and the agent booking another one is the product working, not an
            // incident — it stays visible, in amber.
            <div
              className={`text-sm ${run.tool_errors_unrecovered ? "text-red-600" : "text-amber-600"}`}
              title={
                run.tool_errors_unrecovered
                  ? `${run.tool_errors_unrecovered} outil(s) sans reprise réussie ensuite`
                  : "Rattrapé(s) : un appel du même outil a réussi ensuite"
              }
            >
              {run.tool_errors} outil(s) en erreur
            </div>
          ) : null}
          <div className="ml-auto text-xs text-muted-foreground">
            {expanded ? "masquer" : "détail"}
          </div>
        </button>

        <Checks checks={run.checks?.list || []} />

        {issues.length ? (
          <ul className="px-4 pb-3 space-y-1">
            {issues.map((issue, i) => (
              <li
                key={i}
                className={`text-sm ${i === 0 && run.status === "failed" ? "text-red-600" : "text-muted-foreground"}`}
              >
                • {issue}
              </li>
            ))}
          </ul>
        ) : (
          <div className="px-4 pb-3 text-sm text-muted-foreground">
            {run.goal_note || "Rendez-vous confirmé, aucun incident."}
          </div>
        )}

        {expanded ? <RunDetail runId={run.id} /> : null}
      </CardContent>
    </Card>
  );
}

/**
 * Daily end-to-end booking test — two AIs on a real line.
 *
 * Every morning before opening hours a robot customer calls one of our own agents over a
 * real AudioSocket leg and tries to book an appointment. This page is the history of those
 * calls: the verdict, the delays the robot actually measured on the audio, whether the LLM
 * fallback had to rescue a turn, and any tool that failed — plus the transcript of what was
 * said and what was heard.
 *
 * Alerts go to Sentry from the backend; this page never alerts on its own.
 */
export function AdminSyntheticCallsPage() {
  const { data, isLoading } = useSyntheticCalls(30);
  const run = useRunSyntheticCall();
  const [expanded, setExpanded] = useState<string | null>(null);
  // Folded by default: a green or amber run needs no reading, and thirty of them bury the
  // one that does. "Non concluant" stays visible — the harness broke and nothing was proven.
  const [showPassing, setShowPassing] = useState(false);

  const runs = data?.runs || [];
  const config = data?.config;
  const running = !!data?.running;
  const last = runs[0];
  const failures = runs.filter((r) => r.status === "failed").length;
  const passing = runs.filter(
    (r) => r.status === "ok" || r.status === "degraded",
  ).length;
  const shown = showPassing
    ? runs
    : runs.filter((r) => r.status !== "ok" && r.status !== "degraded");

  const handleRun = async () => {
    try {
      await run.mutateAsync();
      // The call lasts up to three minutes; the list polls itself until the row lands.
      toast.success(
        "Appel de test lancé — le résultat apparaîtra ici dans quelques minutes",
      );
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Appel de test impossible",
      );
    }
  };

  return (
    <AdminPageLayout
      title="Appel de test quotidien"
      description="Un client simulé appelle nos agents et tente de prendre rendez-vous, en audio, sur une vraie ligne"
      actions={[
        <Button
          key="export"
          variant="outline"
          onClick={() =>
            saveCsv(
              () => AdminAPI.getSyntheticCallsCsv({ limit: 200 }),
              "appels-de-test.csv",
            )
          }
          disabled={!runs.length}
          title="Un run par ligne, avec le verdict, les vérifications, les délais et le fallback"
        >
          Exporter l'historique (CSV)
        </Button>,
        <Button
          key="run"
          onClick={handleRun}
          disabled={run.isPending || running || !config?.client_id}
        >
          {running || run.isPending ? "Appel en cours…" : "Lancer maintenant"}
        </Button>,
      ]}
    >

      {config && !config.client_id ? (
        <p className="text-sm text-amber-600">
          Le test est désactivé : aucun tenant de test n'est configuré
          (<code>monitoring.synthetic_call.client_id</code>). L'agent appelé
          répond avec ses vrais outils — un appel réussi crée un vrai rendez-vous
          — il doit donc pointer sur un tenant interne, jamais sur un client.
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">
          Chaque matin ({config?.cron}, UTC), un client simulé appelle le tenant
          de test, en audio, sur {ms(config?.max_duration_ms)} maximum par appel.
          Un run est une suite d'appels enchaînés
          {config?.suite?.length ? ` (${config.suite.join(" → ")})` : ""} : le
          rendez-vous pris au premier appel est celui que les suivants déplacent
          puis annulent, si bien que l'agenda de test finit le run comme il l'a
          commencé. On mesure
          ce qu'il vit : temps avant la première parole de l'agent, délai de
          réponse à chaque tour, intervention du fallback LLM, erreurs d'outil.
          Chaque réponse est notée : 100 % sous {ms(config?.reply_ok_ms)}, 70 %
          jusqu'à {ms(config?.reply_fair_ms)}, 0 % au-delà. C'est le score du run
          qui compte — en dessous de {config?.reply_score_min ?? 80} % il est
          dégradé — et une seule réponse au-delà de {ms(config?.reply_max_ms)}{" "}
          est éliminatoire : le run passe en échec et demande une revue. Les
          échecs partent sur Sentry.
        </p>
      )}

      {running ? (
        <p className="text-sm text-primary">
          Un appel de test est en cours — le résultat s'affichera
          automatiquement.
        </p>
      ) : null}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Dernier appel"
          value={last ? STATUS[last.status]?.label || last.status : "—"}
          hint={last ? when(last.started_at) : "aucun run"}
          tone={
            !last || last.status === "ok"
              ? "default"
              : last.status === "failed"
                ? "bad"
                : "warn"
          }
          isLoading={isLoading}
        />
        <StatCard
          title="Rendez-vous obtenu"
          value={last ? (last.goal_reached ? "oui" : "non") : "—"}
          hint={last?.goal_note}
          tone={last && !last.goal_reached ? "bad" : "default"}
          isLoading={isLoading}
        />
        <StatCard
          title="Réactivité"
          value={last?.latency_grade ? `${last.latency_grade.score}%` : "—"}
          hint={
            last
              ? `p95 ${ms(last.reply_delay?.p95)}, max ${ms(last.reply_delay?.max)}` +
                ` · agent audible en ${ms(last.greeting_ms)}`
              : undefined
          }
          tone={
            !last?.latency_grade
              ? "default"
              : last.latency_grade.overLimit?.length
                ? "bad"
                : last.latency_grade.score < 100
                  ? "warn"
                  : "default"
          }
          isLoading={isLoading}
        />
        <StatCard
          title="Échecs (30 derniers runs)"
          value={failures}
          tone={failures ? "bad" : "default"}
          isLoading={isLoading}
        />
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : runs.length ? (
        <div className="space-y-3">
          {passing ? (
            <div className="flex items-center justify-between gap-4 text-sm text-muted-foreground">
              <span>
                {showPassing
                  ? `${runs.length} run(s) affiché(s)`
                  : `${passing} run(s) réussi(s) ou dégradé(s) masqué(s)`}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPassing(!showPassing)}
              >
                {showPassing ? "Échecs uniquement" : "Tout afficher"}
              </Button>
            </div>
          ) : null}
          {!shown.length ? (
            <p className="text-sm text-muted-foreground">
              Aucun échec sur les {runs.length} derniers runs.
            </p>
          ) : null}
          {shown.map((item) => (
            <RunRow
              key={item.id}
              run={item}
              expanded={expanded === item.id}
              onToggle={() =>
                setExpanded(expanded === item.id ? null : item.id)
              }
            />
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          Aucun appel de test enregistré pour l'instant.
        </p>
      )}
    </AdminPageLayout>
  );
}
